/* eslint-disable no-undef */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { BlurOn, BlurOff } from '@mui/icons-material';
import {
  DISPLAY_VIDEO,
  SHOW_TOOLBAR_CONTEXT_MENU_AFTER,
  THUMBNAIL_TYPE,
  VIDEO_TEST_EVENTS,
  FILMSTRIP_TYPE,
  DISPLAY_MODE_TO_CLASS_NAME,
  computeDisplayModeFromInput,
  getDisplayModeInput,
  setActiveThumbnailPopup,
  getThumbnailTypeFromLayout
} from 'store/modules/filmstrip';
import {
  getLocalParticipant,
  getParticipantByIdOrUndefined,
  hasRaisedHand,
  isScreenShareParticipant,
  isWhiteboardParticipant,
  pinParticipant
} from 'store/modules/base/participants';
import {
  createScreenSharingIssueEvent,
  sendAnalytics
} from 'store/modules/analytics';
import {
  getVideoTrackByParticipant,
  isLocalTrackMuted,
  trackStreamingStatusChanged,
  updateLastTrackVideoMediaEvent
} from 'store/modules/base/tracks';
import { JitsiTrackEvents } from 'store/modules/base/lib-jitsi-meet';
import { LAYOUTS } from 'store/modules/video-layout';
import { ASPECT_RATIO_NARROW } from 'store/modules/base/responsive-ui';
import { MEDIA_TYPE } from 'store/modules/base/media';
import {
  isIosMobileBrowser,
  isMobileBrowser
} from 'store/modules/base/environment/utils';
import { isTestModeEnabled } from 'store/modules/base/testing';
import { selectParticipantInLargeVideo } from 'store/modules/large-video';
import { submitVirtualBackgroundTab } from 'store/modules/settings';
import { VIRTUAL_BACKGROUND_TYPE } from 'store/modules/virtual-background/constants';
import withStyles from '@mui/styles/withStyles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import DisplayName from './DisplayName';
import ThumbnailBottomIndicators from './ThumbnailBottomIndicators';
import VideoTrack from '../Video/VideoTrack';
import Popper from './Popper';
import withBoundary from 'component/ErrorBoundary/withBoundary';

const defaultStyles = (theme) => {
  return {
    indicatorsContainer: {
      position: 'absolute',
      zIndex: 1,
      width: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      left: 0
    },

    indicatorsTopContainer: {
      top: 0,
      justifyContent: 'space-between'
    },

    indicatorsBottomContainer: {
      bottom: 0
    },
    indicatorsBottomRemoteContainer: {
      bottom: 5,
      left: -2
    },

    indicatorsBackground: {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      overflow: 'hidden',
      borderBottomLeftRadius: '4px',
      borderBottomRightRadius: '4px',
      '&:not(:empty)': {
        padding: '4px 8px'
      },
      '&.tile-view-mode': {
        padding: theme.spacing(2)
      },

      '& > *:not(:last-child)': {
        marginRight: '8px'
      }
    },
    indicatorsRemoteBackground: {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      alignItems: 'center',
      width: '100%',
      overflow: 'hidden',
      borderRadius: '5px',
      '&:not(:empty)': {
        padding: '5px'
      },
      '&.tile-view-mode': {
        padding: theme.spacing(2)
      }
    },

    containerBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: '100%',
      borderRadius: '4px',
      backgroundColor: theme.palette.ui02
    },

    borderIndicator: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      zIndex: 9,
      borderRadius: '4px',
      pointerEvents: 'none'
    },
    borderRemoteIndicator: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      zIndex: 9,
      pointerEvents: 'none'
    },

    borderIndicatorOnTop: {
      zIndex: 11
    },

    activeSpeaker: {
      '& .active-speaker-indicator': {
        boxShadow: `inset 0px 0px 0px 1px #14D15B !important`
      }
    },

    raisedHand: {
      '& .raised-hand-border': {
        boxShadow: `inset 0px 0px 0px 1px #14D15B !important`
      }
    },

    sleepModeLocalContainer: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    sleepModeLocalText: {
      ...theme.typography.labelTiny,
      fontWeight: '500',
      alignSelf: 'center',
      color: '#FFF',
      whiteSpace: 'pre-line'
    },

    sleepModeContainer: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      background: `linear-gradient(0deg, #6B6B6B, #6B6B6B)`
    },
    sleepModeText: {
      ...theme.typography.heading6,
      fontWeight: '500',
      alignSelf: 'center',
      color: '#FFF',
      whiteSpace: 'pre-line'
    },
    transparentView: {
      width: global.thumbnailWidth,
      height: global.thumbnailHeight,
      zIndex: 99,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
      borderRadius: '0px 0px 12px 12px',
      padding: '11px !important'
    },
    blurButton: {
      pointerEvents: 'auto',
      cursor: 'pointer',
      padding: '5px',
      position: 'absolute',
      borderRadius: '50%',
      right: 0,
      bottom: 8,
      width: '2.5em',
      height: '2.5em',
      border: 'none',
      zIndex: 1
    }
  };
};

/**
 * Implements a thumbnail.
 *
 * @augments Component
 */
class Thumbnail extends Component {
  /**
   * Initializes a new Thumbnail instance.
   *
   * @param {Object} props - The read-only React Component props with which
   * the new instance is to be initialized.
   */
  constructor(props) {
    super(props);

    const state = {
      canPlayEventReceived: false,
      displayMode: DISPLAY_VIDEO,
      popper: false,
      anchorEl: null
    };

    this.state = {
      ...state,
      displayMode: computeDisplayModeFromInput(
        getDisplayModeInput(props, state)
      )
    };
    this.timeoutHandle = undefined;

    this._clearDoubleClickTimeout = this._clearDoubleClickTimeout.bind(this);
    this._onCanPlay = this._onCanPlay.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onTestingEvent = this._onTestingEvent.bind(this);
    this._toggleBlur = this._toggleBlur.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._showPopover = this._showPopover.bind(this);
    this.handleTrackStreamingStatusChanged =
      this.handleTrackStreamingStatusChanged.bind(this);
  }

  /**
   * Starts listening for track streaming status updates after the initial render.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentDidMount() {
    this._onDisplayModeChanged();

    // Listen to track streaming status changed event to keep it updated.
    // TODO: after converting this component to a react function component,
    // use a custom hook to update local track streaming status.
    const { _videoTrack } = this.props;

    if (_videoTrack && !_videoTrack.local) {
      _videoTrack.jitsiTrack.on(
        JitsiTrackEvents.TRACK_STREAMING_STATUS_CHANGED,
        this.handleTrackStreamingStatusChanged
      );
      this.props._trackStreamingStatusChanged(
        _videoTrack.jitsiTrack,
        _videoTrack.jitsiTrack.getTrackStreamingStatus()
      );
    }
  }

  /**
   * Remove listeners for track streaming status update.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentWillUnmount() {
    // TODO: after converting this component to a react function component,
    // use a custom hook to update local track streaming status.
    const { _videoTrack } = this.props;

    if (_videoTrack && !_videoTrack.local) {
      _videoTrack.jitsiTrack.off(
        JitsiTrackEvents.TRACK_STREAMING_STATUS_CHANGED,
        this.handleTrackStreamingStatusChanged
      );
      this.props._trackStreamingStatusChanged(
        _videoTrack.jitsiTrack,
        _videoTrack.jitsiTrack.getTrackStreamingStatus()
      );
    }
  }

  /**
   * Stops listening for track streaming status updates on the old track and starts
   * listening instead on the new track.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentDidUpdate(prevProps, prevState) {
    if (prevState.displayMode !== this.state.displayMode) {
      this._onDisplayModeChanged();
    }

    // TODO: after converting this component to a react function component,
    // use a custom hook to update local track streaming status.
    const { _videoTrack } = this.props;

    if (
      prevProps._videoTrack?.jitsiTrack?.getSourceName() !==
      _videoTrack?.jitsiTrack?.getSourceName()
    ) {
      if (prevProps._videoTrack && !prevProps._videoTrack.local) {
        prevProps._videoTrack.jitsiTrack.off(
          JitsiTrackEvents.TRACK_STREAMING_STATUS_CHANGED,
          this.handleTrackStreamingStatusChanged
        );
        this.props._trackStreamingStatusChanged(
          prevProps._videoTrack.jitsiTrack,
          prevProps._videoTrack.jitsiTrack.getTrackStreamingStatus()
        );
      }
      if (_videoTrack && !_videoTrack.local) {
        _videoTrack.jitsiTrack.on(
          JitsiTrackEvents.TRACK_STREAMING_STATUS_CHANGED,
          this.handleTrackStreamingStatusChanged
        );
        this.props._trackStreamingStatusChanged(
          _videoTrack.jitsiTrack,
          _videoTrack.jitsiTrack.getTrackStreamingStatus()
        );
      }
    }
  }

  /**
   * Handle track streaming status change event by
   * by dispatching an action to update track streaming status for the given track in app state.
   *
   * @param {JitsiTrack} jitsiTrack - The track with streaming status updated.
   * @param {JitsiTrackStreamingStatus} streamingStatus - The updated track streaming status.
   * @returns {void}
   */
  handleTrackStreamingStatusChanged(jitsiTrack, streamingStatus) {
    this.props._trackStreamingStatusChanged(jitsiTrack, streamingStatus);
  }

  /**
   * Handles display mode changes.
   *
   * @returns {void}
   */
  _onDisplayModeChanged() {
    const input = getDisplayModeInput(this.props, this.state);

    this._maybeSendScreenSharingIssueEvents(input);
  }

  /**
   * Sends screen sharing issue event if an issue is detected.
   *
   * @param {Object} input - The input used to compute the thumbnail display mode.
   * @returns {void}
   */
  _maybeSendScreenSharingIssueEvents(input) {
    const { _isAudioOnly, _isScreenSharing, _thumbnailType } = this.props;
    const { displayMode } = this.state;
    const isTileType = _thumbnailType === THUMBNAIL_TYPE.TILE;

    if (
      !(DISPLAY_VIDEO === displayMode) &&
      isTileType &&
      _isScreenSharing &&
      !_isAudioOnly
    ) {
      sendAnalytics(
        createScreenSharingIssueEvent({
          source: 'thumbnail',
          ...input
        })
      );
    }
  }

  /**
   * Implements React's {@link Component#getDerivedStateFromProps()}.
   *
   * @inheritdoc
   */
  static getDerivedStateFromProps(props, prevState) {
    if (!props._videoTrack && prevState.canPlayEventReceived) {
      const newState = {
        ...prevState,
        canPlayEventReceived: false
      };

      return {
        ...newState,
        displayMode: computeDisplayModeFromInput(
          getDisplayModeInput(props, newState)
        )
      };
    }

    const newDisplayMode = computeDisplayModeFromInput(
      getDisplayModeInput(props, prevState)
    );

    if (newDisplayMode !== prevState.displayMode) {
      return {
        ...prevState,
        displayMode: newDisplayMode
      };
    }

    return null;
  }

  /**
   * Clears the first click timeout.
   *
   * @returns {void}
   */
  _clearDoubleClickTimeout() {
    clearTimeout(this._firstTap);
    this._firstTap = undefined;
  }

  /**
   * Shows popover.
   *
   * @private
   * @returns {void}
   */
  _showPopover(id) {
    this.props._setActiveThumbnailPopup(id);
    this.setState({
      popper: !this.state.popper
    });
  }

  /**
   * Returns an object with the styles for thumbnail.
   *
   * @returns {Object} - The styles for the thumbnail.
   */
  _getStyles() {
    const { canPlayEventReceived } = this.state;
    const {
      _disableTileEnlargement,
      _height,
      _isVirtualScreenshareParticipant,
      _isHidden,
      _isScreenSharing,
      _participant,
      _thumbnailType,
      _videoTrack,
      _userIdleMode,
      _width,
      style,
      fullView
    } = this.props;

    const isTileType = _thumbnailType === THUMBNAIL_TYPE.TILE;
    const track = _videoTrack?.jitsiTrack?.track;
    const isPortraitVideo = (track?.getSettings()?.aspectRatio || 1) < 1;

    let styles = {
      thumbnail: {},
      thumbnailContainer: {},
      avatar: {},
      video: {}
    };

    let videoStyles = null;
    const doNotStretchVideo =
      (isPortraitVideo && isTileType) ||
      _disableTileEnlargement ||
      _isScreenSharing;

    if (
      canPlayEventReceived ||
      _participant.local ||
      _isVirtualScreenshareParticipant
    ) {
      videoStyles = {
        objectFit: doNotStretchVideo ? 'contain' : 'cover'
      };
    } else {
      videoStyles = {
        display: 'none'
      };
    }
    styles.video = videoStyles;
    styles.thumbnail = style ? { ...style } : {};
    if (_participant.local && _userIdleMode) {
      styles.thumbnail = { ...styles.thumbnail, opacity: 0.5 };
    }
    if (!fullView) {
      styles.thumbnail.width = `${_width}px`;
      styles.thumbnail.height = _participant.isRemote
        ? `${_height + 25}px`
        : `${_height}px`;
      styles.thumbnailContainer.width = `${_width}px`;
      styles.thumbnailContainer.height = `${_height}px`;
      styles.video.width = `${_width}px`;
      styles.video.height = `${_height}px`;
    }
    if (_participant.isRemote && !_participant.local && !isTileType) {
      styles.thumbnail.borderRadius = '50%';
      styles.thumbnailContainer.borderRadius = '50%';
      styles.video.borderRadius = '50%';
    }
    if (_isHidden) {
      styles.thumbnail.display = 'none';
    }

    return styles;
  }

  /**
   * On click handler.
   *
   * @returns {void}
   */
  _onClick = (e) => {
    this.setState({ anchorEl: e.currentTarget });
    const {
      _participant,
      _currentLayout,
      _isVideoOnly,
      noOnStage,
      showPopup = true
    } = this.props;
    if (
      !_participant.local &&
      _currentLayout !== LAYOUTS.TILE_VIEW &&
      showPopup
    ) {
      this._showPopover(_participant.id);
      if (_isVideoOnly && !noOnStage) {
        this.pinParticipant();
      }
    }
  };

  /**
   * Attempt to pin or unpin the participant corresponding to this thumbnail.
   *
   * @param {boolean} [onPress=true] - Signals the trigger: direct thumbnail taps leave this `true` for a
   * temporary pin, while the popover pin button sets it to `false` to enable the permanent-pin pathway.
   * @param {boolean} [isPinned=true] - Desired post-action pin state. Thumbnail taps rely on the default
   * `true` to request a non-permanent pin. The popover passes `true` to create a permanent pin or `false`
   * to clear any pin.
   * @returns {void}
   */
  pinParticipant(onPress = true, isPinned = true) {
    const { _participant, _currentLayout, _permanentPinnedParticipant } =
      this.props;
    const {
      id,
      customIsAudioAvailable,
      customSleepModeStats,
      customUserExternal,
      customUserPrivacy,
      local,
      fakeParticipant
    } = _participant;
    if (
      _currentLayout !== LAYOUTS.TILE_VIEW &&
      !local &&
      !fakeParticipant &&
      (!onPress || !_permanentPinnedParticipant)
    ) {
      if (
        customIsAudioAvailable !== 'on' &&
        customSleepModeStats != 'on' &&
        customUserExternal != 'on' &&
        customUserPrivacy != 'on'
      ) {
        this.props._pinParticipant(isPinned ? id : null, !onPress && isPinned);
        if (!onPress) {
          // window.lastPinnedNonRemoteParticipant = _pinnedParticipant;
          this.props._selectParticipantInLargeVideo(id, isPinned);
        }
      }
    }
  }

  /**
   * Handler for touch start.
   *
   * @returns {void}
   */
  _onTouchStart(e) {
    this.setState({ anchorEl: e.currentTarget });
    this.timeoutHandle = window.setTimeout(
      this._showPopover,
      SHOW_TOOLBAR_CONTEXT_MENU_AFTER
    );

    if (this._firstTap) {
      this._clearDoubleClickTimeout();
      this._onClick(e);

      return;
    }

    this._firstTap = window.setTimeout(this._clearDoubleClickTimeout, 300);
  }

  /**
   * Cancel showing popover context menu after x milliseconds if the no. Of milliseconds is not reached yet,
   * or just clears the timeout.
   *
   * @returns {void}
   */
  _onTouchEnd() {
    clearTimeout(this.timeoutHandle);
  }

  /**
   * Cancel showing Context menu after x milliseconds if the number of milliseconds is not reached
   * before a touch move(drag), or just clears the timeout.
   *
   * @returns {void}
   */
  _onTouchMove() {
    clearTimeout(this.timeoutHandle);
  }

  /**
   * Renders a fake participant (youtube video) thumbnail.
   *
   * @param {string} id - The id of the participant.
   * @returns {ReactElement}
   */
  _renderFakeParticipant() {
    const {
      _participant: { avatarURL }
    } = this.props;
    const styles = this._getStyles();
    const containerClassName = this._getContainerClassName();

    return (
      <span
        className={containerClassName}
        id="sharedVideoContainer"
        onClick={this._onClick}
        style={styles.thumbnail}>
        {avatarURL ? (
          <img className="sharedVideoAvatar" src={avatarURL} />
        ) : (
          this._renderAvatar(styles.avatar)
        )}
      </span>
    );
  }

  /**
   * Renders the avatar.
   *
   * @param {Object} styles - The styles that will be applied to the avatar.
   * @returns {ReactElement}
   */
  _renderAvatar() {
    const { _participant, _thumbnailType, classes } = this.props;
    const { id, local, customUserPrivacy, customSleepModeStats } = _participant;
    const isSleepPrivacyMode =
      customUserPrivacy === 'on' || customSleepModeStats === 'on';
    const isThumbnail = _thumbnailType == THUMBNAIL_TYPE.HORIZONTAL;
    return (
      <div className="avatar-container">
        {isSleepPrivacyMode ? (
          <div
            className={
              isThumbnail
                ? classes.sleepModeLocalContainer
                : classes.sleepModeContainer
            }>
            <div
              className={
                isThumbnail ? classes.sleepModeLocalText : classes.sleepModeText
              }>
              {customUserPrivacy === 'on' ? 'In Privacy Mode' : 'In Sleep Mode'}
            </div>
          </div>
        ) : (
          <DisplayName
            allowEditing={false}
            displayNameSuffix=""
            elementID={local ? 'localDisplayName' : `participant_${id}_name`}
            participantID={id}
            thumbnail={isThumbnail}
            thumbnailType={_thumbnailType}
          />
        )}
      </div>
    );
  }

  /**
   * Returns the container class name.
   *
   * @returns {string} - The class name that will be used for the container.
   */
  _getContainerClassName() {
    let className = 'videocontainer thumbnail';
    const { displayMode } = this.state;
    const {
      _isDominantSpeakerDisabled,
      _participant,
      _raisedHand,
      _thumbnailType,
      classes
    } = this.props;

    className += ` ${DISPLAY_MODE_TO_CLASS_NAME[displayMode]}`;

    if (_raisedHand) {
      className += ` ${classes.raisedHand}`;
    }

    if (
      !_isDominantSpeakerDisabled &&
      _participant?.customIsAudioAvailable == 'on'
    ) {
      className += ` ${classes.activeSpeaker} dominant-speaker`;
    }
    if (_thumbnailType !== THUMBNAIL_TYPE.TILE && _participant?.pinned) {
      className += ' videoContainerFocused';
    }

    return className;
  }

  /**
   * Canplay event listener.
   *
   * @param {SyntheticEvent} event - The event.
   * @returns {void}
   */
  _onCanPlay(event) {
    this.setState({ canPlayEventReceived: true });

    const { _isTestModeEnabled, _videoTrack } = this.props;

    if (_videoTrack && _isTestModeEnabled) {
      this._onTestingEvent(event);
    }
  }

  /**
   * Event handler for testing events.
   *
   * @param {SyntheticEvent} event - The event.
   * @returns {void}
   */
  _onTestingEvent(event) {
    const { _videoTrack, dispatch } = this.props;
    const jitsiVideoTrack = _videoTrack?.jitsiTrack;

    dispatch(updateLastTrackVideoMediaEvent(jitsiVideoTrack, event.type));
  }

  /**
   * Toggles the virtual background blur effect for the local participant.
   *
   * @returns {void}
   */
  _toggleBlur() {
    const options = this.props._backgroundEffectEnabled
      ? {
          backgroundEffectEnabled: false,
          selectedThumbnail: 'none'
        }
      : {
          backgroundEffectEnabled: true,
          backgroundType: VIRTUAL_BACKGROUND_TYPE.BLUR,
          blurValue: 25,
          selectedThumbnail: 'blur'
        };
    this.props._toggleBlurSupport({ options });
  }

  /**
   * Renders a remote participant's 'thumbnail.
   *
   * @param {boolean} local - Whether or not it's the local participant.
   * @returns {ReactElement}
   */
  _renderParticipant(local = false) {
    const {
      _disableLocalVideoFlip,
      _isMobilePortrait,
      _isScreenSharing,
      _isTestModeEnabled,
      _localFlipX,
      _participant,
      _thumbnailType,
      _videoTrack,
      classes,
      filmstripType,
      _videoMuted,
      _localParticipant,
      _permanentPinnedParticipant,
      _backgroundEffectEnabled,
      fullView
    } = this.props;
    const { id } = _participant || {};
    const styles = this._getStyles();
    let containerClassName = this._getContainerClassName();
    const videoTrackClassName =
      !_disableLocalVideoFlip && _videoTrack && !_isScreenSharing && _localFlipX
        ? 'flipVideoX'
        : '';
    const jitsiVideoTrack = _videoTrack?.jitsiTrack;
    const videoTrackId = jitsiVideoTrack?.getId();
    const videoEventListeners = {};
    const isTileType = _thumbnailType === THUMBNAIL_TYPE.TILE;

    if (local) {
      if (_isMobilePortrait) {
        styles.thumbnail.height = styles.thumbnail.width;
        containerClassName = `${containerClassName} self-view-mobile-portrait`;
      }
    } else {
      if (_videoTrack && _isTestModeEnabled) {
        VIDEO_TEST_EVENTS.forEach((attribute) => {
          videoEventListeners[attribute] = this._onTestingEvent;
        });
      }
      videoEventListeners.onCanPlay = this._onCanPlay;
    }
    const video = !_videoMuted && !_videoTrack?.muted && (
      <VideoTrack
        className={local ? videoTrackClassName : ''}
        eventHandlers={videoEventListeners}
        id={
          local ? 'localVideo_container' : `remoteVideo_${videoTrackId || ''}`
        }
        muted={local ? undefined : true}
        style={styles.video}
        videoTrack={_videoTrack}
      />
    );
    const spanId = local
      ? `localVideoContainer${
          filmstripType === FILMSTRIP_TYPE.MAIN ? '' : `_${filmstripType}`
        }`
      : `participant_${id}${
          filmstripType === FILMSTRIP_TYPE.MAIN ? '' : `_${filmstripType}`
        }`;
    return (
      <span
        aria-describedby={id}
        className={containerClassName}
        onClick={this._onClick}
        onTouchEnd={this._onTouchEnd}
        onTouchMove={this._onTouchMove}
        onTouchStart={this._onTouchStart}
        id={spanId}
        style={styles.thumbnail}>
        {this.state.popper && (
          <Popper
            localParticipant={_localParticipant}
            anchorEl={this.state.anchorEl}
            isRemoteParticipant={_participant?.isRemote}
            onClose={() => this.setState({ popper: false, anchorEl: null })}
            pinned={_permanentPinnedParticipant === id}
            onPin={(pinned) => this.pinParticipant(false, pinned)}
            participant={_participant}
          />
        )}
        {local ? <span id="localVideoWrapper">{video}</span> : video}
        {!local && (
          <div
            className="thumbnail-container"
            style={styles.thumbnailContainer}>
            {(!_participant.isRemote || isTileType) && !fullView && (
              <div
                className={clsx(
                  classes.indicatorsContainer,
                  classes.indicatorsBottomContainer,
                  isTileType && 'tile-view-mode'
                )}>
                <ThumbnailBottomIndicators
                  className={clsx(
                    classes.indicatorsBackground,
                    isTileType && 'tile-view-mode'
                  )}
                  local={local}
                  participantId={id}
                  showStatusIndicators={!isWhiteboardParticipant(_participant)}
                  thumbnailType={_thumbnailType}
                />
              </div>
            )}
            {this._renderAvatar(styles.avatar)}
            <div
              className={clsx(
                _participant.isRemote
                  ? classes.borderRamoteIndicator
                  : classes.borderIndicator,
                'raised-hand-border'
              )}
            />
            <div
              className={clsx(
                _participant.isRemote
                  ? classes.borderRamoteIndicator
                  : classes.borderIndicator,
                'active-speaker-indicator'
              )}
            />
          </div>
        )}
        {_participant.isRemote &&
          !local &&
          _thumbnailType !== THUMBNAIL_TYPE.TILE && (
            <div
              className={clsx(
                classes.indicatorsContainer,
                classes.indicatorsBottomContainer,
                isTileType && 'tile-view-mode'
              )}
              style={{ left: 0 }}>
              <ThumbnailBottomIndicators
                className={clsx(
                  classes.indicatorsRemoteBackground,
                  isTileType && 'tile-view-mode'
                )}
                local={local}
                participantId={id}
                showStatusIndicators={true}
                thumbnailType={_thumbnailType}
              />
            </div>
          )}
        {_participant?.isRemote && !isIosMobileBrowser() && local && (
          <button
            className={classes.blurButton}
            style={{
              backgroundColor: _backgroundEffectEnabled ? '#14D15B' : 'grey'
            }}
            onClick={this._toggleBlur}>
            {_backgroundEffectEnabled ? (
              <BlurOn color="white" fontSize="small" />
            ) : (
              <BlurOff color="white" fontSize="small" />
            )}
          </button>
        )}
      </span>
    );
  }

  /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
  render() {
    const { _participant, _isVirtualScreenshareParticipant } = this.props;

    if (!_participant) {
      return null;
    }

    const { fakeParticipant, local } = _participant;

    if (local) {
      return this._renderParticipant(true);
    }

    if (
      fakeParticipant &&
      !isWhiteboardParticipant(_participant) &&
      !_isVirtualScreenshareParticipant
    ) {
      return this._renderFakeParticipant();
    }

    return this._renderParticipant();
  }
}

/**
 * Maps (parts of) the redux state to the associated props for this component.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The own props of the component.
 * @private
 * @returns {IProps}
 */
function _mapStateToProps(state, ownProps) {
  const {
    participantID,
    filmstripType = FILMSTRIP_TYPE.MAIN,
    _currentLayout
  } = ownProps;
  const participant = getParticipantByIdOrUndefined(state, participantID);
  const isLocal = participant?.local ?? true;
  const tracks = state['features/base/tracks'];
  const _videoTrack = getVideoTrackByParticipant(state, participant);
  let size = {};
  let _isMobilePortrait = false;
  const {
    disableLocalVideoFlip,
    disableTileEnlargement,
    iAmRecorder,
    iAmSipGateway
  } = state['features/base/config'];
  const { localFlipX } = state['features/base/settings'];
  const _isMobile = isMobileBrowser();
  const tileType = getThumbnailTypeFromLayout(_currentLayout, filmstripType);
  switch (tileType) {
    case THUMBNAIL_TYPE.HORIZONTAL: {
      const {
        horizontalViewDimensions = {
          local: {},
          remote: {}
        }
      } = state['features/filmstrip'];
      const { local, remote } = horizontalViewDimensions;

      const { width: _width, height: _height } =
        (isLocal ? local : remote) ?? {};

      size = {
        _width,
        _height
      };
      _isMobilePortrait =
        _isMobile &&
        state['features/base/responsive-ui'].aspectRatio ===
          ASPECT_RATIO_NARROW;

      break;
    }
    case THUMBNAIL_TYPE.TILE: {
      const { thumbnailSize } = state['features/filmstrip'].tileViewDimensions;
      const {
        stageFilmstripDimensions = {
          thumbnailSize: {}
        },
        screenshareFilmstripDimensions
      } = state['features/filmstrip'];
      size = {
        _width: thumbnailSize?.width,
        _height: thumbnailSize?.height
      };

      if (filmstripType === FILMSTRIP_TYPE.STAGE) {
        const { width: _width, height: _height } =
          stageFilmstripDimensions.thumbnailSize ?? {};

        size = {
          _width,
          _height
        };
      } else if (filmstripType === FILMSTRIP_TYPE.SCREENSHARE) {
        const { width: _width, height: _height } =
          screenshareFilmstripDimensions.thumbnailSize ?? {};

        size = {
          _width,
          _height
        };
      }
      break;
    }
  }

  if (ownProps.width) {
    size._width = ownProps.width;
  }
  if (ownProps.height) {
    size._height = ownProps.height;
  }

  return {
    _currentLayout,
    _permanentPinnedParticipant:
      state['features/base/participants'].permanentPinnedParticipant,
    _pinnedParticipant: state['features/base/participants'].pinnedParticipant,
    _disableLocalVideoFlip: Boolean(disableLocalVideoFlip),
    _disableTileEnlargement: Boolean(disableTileEnlargement),
    _userIdleMode: state['features/settings'].userIdleMode,
    _localParticipant: getLocalParticipant(state),
    _isHidden: isLocal && iAmRecorder && !iAmSipGateway,
    _isAudioOnly: Boolean(state['features/base/audio-only'].enabled),
    _isVideoOnly: state['features/settings'].videoOnly,
    _isDominantSpeakerDisabled:
      interfaceConfig.DISABLE_DOMINANT_SPEAKER_INDICATOR,
    _isMobile,
    _isMobilePortrait,
    _isScreenSharing: _videoTrack?.videoType === 'desktop',
    _isTestModeEnabled: isTestModeEnabled(state),
    _isVirtualScreenshareParticipant: isScreenShareParticipant(participant),
    _videoMuted: isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO),
    _localFlipX: Boolean(localFlipX),
    _participant: participant,
    _raisedHand: hasRaisedHand(participant),
    _thumbnailType: tileType,
    _videoTrack,
    _backgroundEffectEnabled:
      state['feature/virtual-background'].backgroundEffectEnabled,
    ...size
  };
}
/**
 * Maps dispatching of some actions to React component props.
 *
 * @param {Function} dispatch - Redux action dispatcher.
 * @private
 * @returns {{
 *     _toggleBlurSupport: void,
 * }}
 */
export function _mapDispatchToProps(dispatch) {
  return {
    _toggleBlurSupport: (options, isCancel) =>
      dispatch(submitVirtualBackgroundTab(options, isCancel)),
    _trackStreamingStatusChanged: (track, status) =>
      dispatch(trackStreamingStatusChanged(track, status)),
    _setActiveThumbnailPopup: (active) =>
      dispatch(setActiveThumbnailPopup(active)),
    _pinParticipant: (id, permanent) => dispatch(pinParticipant(id, permanent)),
    _selectParticipantInLargeVideo: (id, pinned) =>
      dispatch(selectParticipantInLargeVideo(id, pinned))
  };
}
export default React.memo(
  connect(
    _mapStateToProps,
    _mapDispatchToProps
  )(withStyles(defaultStyles)(withBoundary(Thumbnail)))
);
/**
 * The type of the React {@code Component} props of {@link Thumbnail}.
 */
Thumbnail.propTypes = {
  /**
   * The audio track related to the participant.
   */
  _audioTrack: PropTypes.object,

  /**
   * Indicates whether the local video flip feature is disabled or not.
   */
  _disableLocalVideoFlip: PropTypes.bool,

  /**
   * Indicates whether enlargement of tiles to fill the available space is disabled.
   */
  _disableTileEnlargement: PropTypes.bool,

  /**
   * URL of GIF sent by this participant, null if there's none.
   */
  _gifSrc: PropTypes.string,

  /**
   * The height of the Thumbnail.
   */
  _height: PropTypes.number,

  /**
   * Whether or not the participant is displayed on the stage filmstrip.
   * Used to hide the video from the vertical filmstrip.
   */
  _isActiveParticipant: PropTypes.bool,

  /**
   * Indicates whether audio only mode is enabled.
   */
  _isAudioOnly: PropTypes.bool,
  _videoMuted: PropTypes.bool,
  _isVideoOnly: PropTypes.bool,

  /**
   * Indicates whether the participant associated with the thumbnail is displayed on the large video.
   */
  _isCurrentlyOnLargeVideo: PropTypes.bool,

  /**
   * Disable/enable the dominant speaker indicator.
   */
  _isDominantSpeakerDisabled: PropTypes.bool,

  /**
   * Indicates whether the thumbnail should be hidden or not.
   */
  _isHidden: PropTypes.bool,

  /**
   * Whether we are currently running in a mobile browser.
   */
  _isMobile: PropTypes.bool,

  /**
   * Whether we are currently running in a mobile browser in portrait orientation.
   */
  _isMobilePortrait: PropTypes.bool,

  /**
   * Indicates whether the participant is screen sharing.
   */
  _isScreenSharing: PropTypes.bool,

  /**
   * Indicates whether testing mode is enabled.
   */
  _isTestModeEnabled: PropTypes.bool,

  /**
   * Indicates whether the video associated with the thumbnail is playable.
   */
  _isVideoPlayable: PropTypes.bool,

  /**
   * Indicates whether the participant is a virtual screen share participant. This prop is behind the
   * sourceNameSignaling feature flag.
   */
  _isVirtualScreenshareParticipant: PropTypes.bool,

  /**
   * The current local video flip setting.
   */
  _localFlipX: PropTypes.bool,

  /**
   * An object with information about the participant related to the thumbnail.
   */
  _participant: PropTypes.object,
  _localParticipant: PropTypes.object,
  _pinnedParticipant: PropTypes.string,
  _permanentPinnedParticipant: PropTypes.string,

  /**
   * Whether or not the participant has the hand raised.
   */
  _raisedHand: PropTypes.bool,

  /**
   * Whether or not the current layout is stage filmstrip layout.
   */
  _stageFilmstripLayout: PropTypes.bool,
  _currentLayout: PropTypes.any,
  /**
   * Whether or not the participants are displayed on stage.
   * (and not screensharing or shared video; used to determine
   * whether or not the display the participant video in the vertical filmstrip).
   */
  _stageParticipantsVisible: PropTypes.bool,

  /**
   * The type of thumbnail to display.
   */
  _thumbnailType: PropTypes.string,

  /**
   * The video object position for the participant.
   */
  _videoObjectPosition: PropTypes.string,

  /**
   * The video track that will be displayed in the thumbnail.
   */
  _videoTrack: PropTypes.any,

  /**
   * The width of the thumbnail.
   */
  _width: PropTypes.number,

  /**
   * An object containing CSS classes.
   */
  classes: PropTypes.any,

  /**
   * The redux dispatch function.
   */
  dispatch: PropTypes.func,

  /**
   * The type of filmstrip the tile is displayed in.
   */
  filmstripType: PropTypes.string,

  /**
   * The horizontal offset in px for the thumbnail. Used to center the thumbnails from the last row in tile view.
   */
  horizontalOffset: PropTypes.number,

  /**
   * The ID of the participant related to the thumbnail.
   */
  participantID: PropTypes.string,

  /**
   * Styles that will be set to the Thumbnail's main span element.
   */
  style: PropTypes.any,

  /**
   * The width of the thumbnail. Used for expanding the width of the thumbnails on last row in case
   * there is empty space.
   */
  width: PropTypes.number,
  fullView: PropTypes.bool,
  showPopup: PropTypes.bool,
  noOnStage: PropTypes.bool,
  _userIdleMode: PropTypes.bool,
  _backgroundEffectEnabled: PropTypes.bool,
  _toggleBlurSupport: PropTypes.func,
  _trackStreamingStatusChanged: PropTypes.func,
  _setActiveThumbnailPopup: PropTypes.func,
  _pinParticipant: PropTypes.func,
  _selectParticipantInLargeVideo: PropTypes.func
};
